import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Edit,
  Download,
  Share,
  Printer,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ViewTask = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [subtasks, setSubtasks] = useState([]);
  
  // Task data
  const [taskData, setTaskData] = useState({
    // Basic Information
    title: 'Implement user authentication system',
    description: 'Develop secure login and registration functionality with JWT tokens and proper validation. This includes user registration, login, password reset, and session management.',
    project: 'Digital Transformation Initiative',
    status: 'In Progress',
    priority: 'High',
    assignee: 'Sarah Johnson',
    createdBy: 'John Doe',
    
    // Timeline & Effort
    dueDate: '2024-02-15',
    startDate: '2024-01-20',
    estimatedHours: 40,
    actualHours: 25,
    progress: 65,
    
    // Task Details
    tags: ['Frontend', 'Security', 'Authentication'],
    dependencies: [
      {
        task: 'Setup development environment',
        type: 'Blocks',
        description: 'Development environment must be ready before starting authentication implementation'
      },
      {
        task: 'Database schema design',
        type: 'Blocked By',
        description: 'User table structure needs to be finalized'
      }
    ],
    comments: [
      {
        text: 'Initial setup completed, ready to start implementation',
        author: 'Sarah Johnson',
        createdAt: '2024-01-20T10:00:00Z'
      },
      {
        text: 'Need to review security requirements with the team',
        author: 'John Doe',
        createdAt: '2024-01-22T14:30:00Z'
      },
      {
        text: 'JWT implementation is progressing well, should be ready for testing by end of week',
        author: 'Sarah Johnson',
        createdAt: '2024-01-25T16:45:00Z'
      }
    ],
    attachments: [
      {
        name: 'auth-requirements.pdf',
        type: 'document',
        url: '/uploads/auth-requirements.pdf',
        uploadedBy: 'Sarah Johnson',
        uploadedDate: '2024-01-20T09:00:00Z'
      },
      {
        name: 'jwt-implementation-guide.md',
        type: 'document',
        url: '/uploads/jwt-guide.md',
        uploadedBy: 'John Doe',
        uploadedDate: '2024-01-21T11:15:00Z'
      },
      {
        name: 'security-checklist.xlsx',
        type: 'document',
        url: '/uploads/security-checklist.xlsx',
        uploadedBy: 'Sarah Johnson',
        uploadedDate: '2024-01-23T13:20:00Z'
      }
    ],
    
    // Advanced Settings
    isActive: true,
    customFields: []
  });

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Task Setup',
      icon: <FileText size={18} />
    },
    {
      id: 'timeline',
      label: 'Timeline & Effort',
      icon: <Clock size={18} />
    },
    {
      id: 'details',
      label: 'Task Details',
      icon: <Target size={18} />
    },
    {
      id: 'advanced',
      label: 'Advanced Settings',
      icon: <Settings size={18} />
    }
  ];

  useEffect(() => {
    if (id) {
      loadTask();
    }
  }, [id]);

  const loadTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}/${id}`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        const task = data.data?.task || data.task || data;
        const taskSubtasks = data.data?.subtasks || [];
        
        // Handle assignee - use assigneeName if available, otherwise populated assignee
        const assigneeDisplay = task.assigneeName || 
                                (task.assignee 
                                  ? (typeof task.assignee === 'object' 
                                      ? (task.assignee.name || task.assignee.email || 'Unassigned')
                                      : task.assignee)
                                  : 'Unassigned');

        // Handle createdBy - use createdByName if available, otherwise populated createdBy
        const createdByDisplay = task.createdByName || 
                                 (task.createdBy 
                                   ? (typeof task.createdBy === 'object' 
                                       ? (task.createdBy.name || task.createdBy.email || 'Unknown')
                                       : task.createdBy)
                                   : 'Unknown');

        // Format dates
        const formatDateForDisplay = (dateString) => {
          if (!dateString) return '';
          try {
            return new Date(dateString).toISOString().split('T')[0];
          } catch {
            return '';
          }
        };

        setTaskData({
          title: task.title || '',
          description: task.description || '',
          project: task.project?.name || 'No Project',
          projectCode: task.project?.projectCode || '',
          parentTask: task.parentTask?._id || task.parentTask,
          parentTaskTitle: task.parentTask?.title || '',
          status: task.status || 'To Do',
          priority: task.priority || 'Medium',
          assignee: assigneeDisplay,
          createdBy: createdByDisplay,
          dueDate: formatDateForDisplay(task.dueDate),
          startDate: formatDateForDisplay(task.startDate),
          completedDate: formatDateForDisplay(task.completedDate),
          estimatedHours: task.estimatedHours || 0,
          actualHours: task.actualHours || 0,
          progress: task.progress || 0,
          tags: task.tags || [],
          dependencies: task.dependencies || [],
          comments: task.comments || [],
          attachments: task.attachments || [],
          isActive: task.isActive !== undefined ? task.isActive : true
        });

        // Set subtasks
        setSubtasks(taskSubtasks.map(subtask => ({
          id: subtask._id || subtask.id,
          title: subtask.title || '',
          status: subtask.status || 'To Do',
          assignee: subtask.assigneeName || 
                   (subtask.assignee 
                     ? (typeof subtask.assignee === 'object' 
                         ? (subtask.assignee.name || subtask.assignee.email || 'Unassigned')
                         : subtask.assignee)
                     : 'Unassigned'),
          progress: subtask.progress || 0,
          dueDate: formatDateForDisplay(subtask.dueDate)
        })));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load task');
      }
    } catch (err) {
      console.error('Error loading task:', err);
      showError(err.message || 'Failed to load task details');
      navigate('/projects/task-management');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/projects/task-management/edit/${id}`);
  };

  const handleDownload = () => {
    // Implement download functionality
    success('Task details downloaded successfully!');
  };

  const handleShare = () => {
    // Implement share functionality
    success('Task link copied to clipboard!');
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'To Do': 'secondary',
      'In Progress': 'primary',
      'Review': 'warning',
      'Done': 'success',
      'Cancelled': 'danger'
    };
    return statusConfig[status] || 'secondary';
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'Low': 'success',
      'Medium': 'warning',
      'High': 'danger',
      'Critical': 'danger'
    };
    return priorityConfig[priority] || 'secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  // Render functions for each tab
  const renderBasicInformation = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Task title, description, and basic details</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Task Title</label>
        <div className="form-control-static">{taskData.title}</div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <div className="form-control-static">{taskData.description}</div>
      </div>
      
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Project</label>
          <div className="form-control-static">
            {taskData.projectCode && <span className="text-muted">{taskData.projectCode} - </span>}
            {taskData.project}
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <div className="form-control-static">
            <span className={`badge badge-${getStatusBadge(taskData.status)}`}>
              {taskData.status}
            </span>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <div className="form-control-static">
            <span className={`badge badge-${getPriorityBadge(taskData.priority)}`}>
              {taskData.priority}
            </span>
          </div>
        </div>
      </div>
      
      {taskData.parentTaskTitle && (
        <div className="form-group">
          <label className="form-label">Parent Task</label>
          <div className="form-control-static">
            <div className="parent-task-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link size={16} />
              <span>{taskData.parentTaskTitle}</span>
            </div>
          </div>
        </div>
      )}
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Assignee</label>
          <div className="form-control-static">
            <div className="assignee-info">
              <User size={16} />
              <span>{taskData.assignee}</span>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Created By</label>
          <div className="form-control-static">
            <div className="creator-info">
              <User size={16} />
              <span>{taskData.createdBy}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimelineEffort = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline & Effort</h3>
        <p className="section-description">Schedule, duration, and effort tracking</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <div className="form-control-static">
            <div className="date-info">
              <Calendar size={16} />
              <span>{formatDate(taskData.startDate)}</span>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Due Date</label>
          <div className="form-control-static">
            <div className="date-info">
              <Calendar size={16} />
              <span>{formatDate(taskData.dueDate)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Estimated Hours</label>
          <div className="form-control-static">
            <div className="hours-info">
              <Clock size={16} />
              <span>{taskData.estimatedHours} hours</span>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Actual Hours</label>
          <div className="form-control-static">
            <div className="hours-info">
              <Clock size={16} />
              <span>{taskData.actualHours} hours</span>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Progress</label>
          <div className="form-control-static">
            <div className="progress-info">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${taskData.progress}%` }}
                />
              </div>
              <span className="progress-text">{taskData.progress}%</span>
            </div>
          </div>
        </div>
      </div>
      
      {taskData.estimatedHours > 0 && (
        <div className="form-group">
          <label className="form-label">Time Remaining</label>
          <div className="form-control-static">
            {taskData.estimatedHours - taskData.actualHours > 0 
              ? `${taskData.estimatedHours - taskData.actualHours} hours remaining`
              : 'Task completed or over estimated time'
            }
          </div>
        </div>
      )}

      {taskData.completedDate && (
        <div className="form-group">
          <label className="form-label">Completed Date</label>
          <div className="form-control-static">
            <div className="date-info">
              <CheckCircle size={16} />
              <span>{formatDate(taskData.completedDate)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTaskDetails = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Task Tags</h3>
        <p className="section-description">Tags used to categorize this task</p>
      </div>
      
      {subtasks.length > 0 && (
        <>
          <div className="section-header">
            <h3 className="section-title">Subtasks</h3>
            <p className="section-description">Child tasks of this task</p>
          </div>
          
          <div className="form-group">
            <label className="form-label">Subtasks</label>
            <div className="subtasks-container" style={{ marginBottom: '24px' }}>
              {subtasks.map((subtask) => (
                <div key={subtask.id} className="subtask-item" style={{ 
                  padding: '12px', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '4px',
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>{subtask.title}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Assignee: {subtask.assignee} | 
                        {subtask.dueDate && ` Due: ${formatDate(subtask.dueDate)}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span className={`badge badge-${getStatusBadge(subtask.status)}`}>
                        {subtask.status}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {subtask.progress}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="section-header">
        <h3 className="section-title">Task Tags</h3>
        <p className="section-description">Tags used to categorize this task</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Tags</label>
        <div className="form-control-static">
          <div className="tags-container">
            {taskData.tags && taskData.tags.length > 0 ? (
              taskData.tags.map((tag, index) => (
                <span key={index} className="badge badge-primary me-2">{tag}</span>
              ))
            ) : (
              <span style={{ color: '#6b7280' }}>No tags assigned</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Task Dependencies</h3>
        <p className="section-description">Relationships with other tasks</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Dependencies</label>
        <div className="dependencies-container">
          {taskData.dependencies && taskData.dependencies.length > 0 ? (
            taskData.dependencies.map((dependency, index) => (
              <div key={index} className="dependency-item">
                <div className="dependency-header">
                  <span className="dependency-task">
                    {typeof dependency.task === 'object' 
                      ? (dependency.task?.title || dependency.task?._id || 'Unknown Task')
                      : dependency.task || 'Unknown Task'}
                  </span>
                  {dependency.type && (
                    <span className={`badge badge-${dependency.type === 'Blocks' ? 'danger' : dependency.type === 'Blocked By' ? 'warning' : 'info'}`}>
                      {dependency.type}
                    </span>
                  )}
                </div>
                {dependency.description && (
                  <div className="dependency-description">{dependency.description}</div>
                )}
              </div>
            ))
          ) : (
            <span style={{ color: '#6b7280' }}>No dependencies</span>
          )}
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Comments & Attachments</h3>
        <p className="section-description">Task comments and attached files</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Comments</label>
        <div className="comments-container">
          {taskData.comments && taskData.comments.length > 0 ? (
            taskData.comments.map((comment, index) => {
              const authorName = comment.author 
                ? (typeof comment.author === 'object' 
                    ? (comment.author.name || comment.author.email || 'Unknown')
                    : comment.author)
                : 'Unknown';
              
              return (
                <div key={index} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-author">{authorName}</span>
                    <span className="comment-date">
                      {comment.createdAt ? formatDateTime(comment.createdAt) : 'Unknown date'}
                    </span>
                  </div>
                  <div className="comment-text">{comment.text || ''}</div>
                </div>
              );
            })
          ) : (
            <span style={{ color: '#6b7280' }}>No comments</span>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Attachments</label>
        <div className="attachments-container">
          {taskData.attachments && taskData.attachments.length > 0 ? (
            taskData.attachments.map((attachment, index) => {
              const uploadedByName = attachment.uploadedBy
                ? (typeof attachment.uploadedBy === 'object'
                    ? (attachment.uploadedBy.name || attachment.uploadedBy.email || 'Unknown')
                    : attachment.uploadedBy)
                : 'Unknown';
              
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-header">
                    <div className="attachment-info">
                      <Paperclip size={16} />
                      <span className="attachment-name">{attachment.name || 'Unnamed file'}</span>
                      {attachment.type && (
                        <span className={`badge badge-${attachment.type === 'document' ? 'primary' : attachment.type === 'image' ? 'success' : 'secondary'}`}>
                          {attachment.type}
                        </span>
                      )}
                    </div>
                    <div className="attachment-meta">
                      <span>Uploaded by {uploadedByName}</span>
                      {attachment.uploadedDate && (
                        <span>{formatDateTime(attachment.uploadedDate)}</span>
                      )}
                    </div>
                    {attachment.url && (
                      <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline">
                        View
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <span style={{ color: '#6b7280' }}>No attachments</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Advanced Settings</h3>
        <p className="section-description">Additional task configuration and metadata</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Task Status</label>
        <div className="form-control-static">
          <span className={`badge badge-${taskData.isActive ? 'success' : 'secondary'}`}>
            {taskData.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Task ID</label>
        <div className="form-control-static">TASK-{id || '001'}</div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Last Updated</label>
        <div className="form-control-static">{formatDateTime(new Date().toISOString())}</div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInformation();
      case 'timeline':
        return renderTimelineEffort();
      case 'details':
        return renderTaskDetails();
      case 'advanced':
        return renderAdvancedSettings();
      default:
        return renderBasicInformation();
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="View Task"
          description="Loading task details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', marginBottom: '24px' }}>
        <PageHeader 
          title="View Task"
          description={taskData.title || "Task details"}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-outline"
            onClick={handleDownload}
          >
            <Download size={18} />
            Download
          </button>
          <button
            className="btn btn-outline"
            onClick={handleShare}
          >
            <Share size={18} />
            Share
          </button>
          <button
            className="btn btn-outline"
            onClick={handlePrint}
          >
            <Printer size={18} />
            Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleEdit}
          >
            <Edit size={18} />
            Edit Task
          </button>
        </div>
      </div>
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="task-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Tab Navigation */}
        <div className="task-tabs">
          <div className="task-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`task-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="task-tab-icon">
                  {tab.icon}
                </div>
                <div className="task-tab-content">
                  <div className="task-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="task-tab-content">
          <div className="task-form-card">
            {renderTabContent()}
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default ViewTask;
